import { getEventDetailsDesktop } from "app/api/eventsApi";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import Markets from "app/components/UnsummarizedMarketsModal/components/ModalMarkets/ModalMarkets";
import { SessionHelper } from "app/helpers/sessionHelper";
import type { EventMarketPeriodType, EventType } from "app/types/eventType";
import { UserType } from "app/types/userType";
import { EventStatus, PollingIntervals } from "app/utils/const";
import isPublicView from "app/utils/publicViewUtils";
import flatten from "lodash/flatten";
import React, { Component } from "react";
import { connect } from "react-redux";
import WidgetAvailable from "./components/WidgetAvailable/WidgetAvailable";
import WidgetNotAvailable from "./components/WidgetNotAvailable/WidgetNotAvailable";
import "./UnsummarizedMarketsScreen.scss";
//
type Props = {
  userTimezone: Record<string, any>;
  marketGroupType: string;
  subscribeMarkets: (...args: Array<any>) => any;
  unsubscribeMarkets: (...args: Array<any>) => any;
  betslipMarketIds: number[];
  isIdle: boolean;
  unsummarizedMarketsModalEvent: EventType;
  updateUnsummarizedMarketsModalEvent: (...args: Array<any>) => any;
  subscribeEvent: (...args: Array<any>) => any;
  unsubscribeEvent: (...args: Array<any>) => any;
  darkMode: boolean;
  widgetExpanded: boolean;
  toggleWidget: (widgetExpanded: boolean) => void;
  user: UserType;
};
type State = {
  event: EventType;
  loading: boolean;
  loadingMarketData: boolean;
};

// Euro unsummarised event
class UnsummarizedMarketsScreen extends Component<Props, State> {
  intervalId: IntervalID | null;
  pollLoading: boolean;

  constructor(props) {
    super(props);
    this.state = {
      event: {},
      loading: true,
      loadingMarketData: false,
    };
    this.pollLoading = false;
    this.intervalId = null;
    this.startIntervalFetchEvent();
    this.fetchEventData();
  }

  componentDidUpdate = (prevProps: any, prevState: any) => {
    const {
      marketGroupType,
      unsummarizedMarketsModalEvent,
      updateUnsummarizedMarketsModalEvent,
      subscribeEvent,
    } = this.props;

    if (prevProps.marketGroupType !== marketGroupType) {
      this.fetchEventDataByMarketGroup();
    }

    if (unsummarizedMarketsModalEvent.status === EventStatus.CLOSED) {
      updateUnsummarizedMarketsModalEvent(null);
    }

    if (
      unsummarizedMarketsModalEvent !== prevProps.unsummarizedMarketsModalEvent
    ) {
      subscribeEvent(unsummarizedMarketsModalEvent.id);
    }
    // Check if the user changed the odds type in public view and is not in Asian view.
    if (
      isPublicView() &&
      !SessionHelper.isAsianRouteAndSessionView() &&
      this.props.user?.preferences?.oddsType !==
        prevProps.user?.preferences?.oddsType
    ) {
      console.log("odds type changed");
      this.fetchEventData();
    }
  };

  componentWillUnmount = () => {
    const {
      updateUnsummarizedMarketsModalEvent,
      unsummarizedMarketsModalEvent,
      unsubscribeEvent,
    } = this.props;
    this.unsubscribeModalMarkets();
    this.endIntervalFetchEvent();
    unsubscribeEvent(unsummarizedMarketsModalEvent.id);
    updateUnsummarizedMarketsModalEvent(null);
  };
  // fetch data while displaying loading spinner during fetch
  fetchEventDataByMarketGroup = () => {
    this.fetchEventData();
    this.setState({
      loadingMarketData: true,
    });
  };

  fetchEventData = () => {
    const {
      unsummarizedMarketsModalEvent,
      updateUnsummarizedMarketsModalEvent,
      marketGroupType,
      subscribeMarkets,
    } = this.props;

    getEventDetailsDesktop(
      unsummarizedMarketsModalEvent.id,
      true,
      false,
      "ALL",
      null,
      marketGroupType
    )
      .then(eventData => {
        updateUnsummarizedMarketsModalEvent(eventData);
        const currMarketIds = this.getListOfMarketIDs(eventData.marketPeriods);
        subscribeMarkets(currMarketIds);
        this.pollLoading = false;
        this.setState({
          event: eventData,
          loading: false,
          loadingMarketData: false,
        });
      })
      .catch(error => {
        throw error;
      });
  };

  // Move to helper
  getListOfMarketIDs(marketPeriods: EventMarketPeriodType[]) {
    // Takes a list of marketPeriods
    // Returns a list of market ids for each market in each marketPeriod
    if (marketPeriods && marketPeriods.length > 0) {
      const marketTypes = flatten(marketPeriods.map(mp => mp.marketTypes));
      return (
        flatten(marketTypes.map(mt => mt.markets))
          .map(m => m.id)
          .filter(m => m != null) || []
      );
    } else {
      return [];
    }
  }

  /**
   * Unsubscribe modal markets except
   * markets in the betslip
   */
  unsubscribeModalMarkets() {
    const { unsummarizedMarketsModalEvent, betslipMarketIds } = this.props;
    const marketIds = this.getListOfMarketIDs(
      unsummarizedMarketsModalEvent.marketPeriods
    );
    betslipMarketIds.forEach(bsmid => {
      const index = marketIds.indexOf(bsmid);
      if (index !== -1) marketIds.splice(index, 1);
    });
    this.props.unsubscribeMarkets(marketIds);
  }

  /**
   * update event every 30 sec
   */
  startIntervalFetchEvent = () => {
    const { isIdle } = this.props;

    if (!this.intervalId) {
      this.intervalId = setInterval(() => {
        // if user session idle stop polling
        if (isIdle || this.pollLoading) return;
        this.pollLoading = true;
        this.fetchEventData();
      }, PollingIntervals.EVENT);
    }
  };
  endIntervalFetchEvent = () => {
    clearInterval(this.intervalId);
    this.intervalId = null;
  };

  render() {
    const {
      darkMode,
      unsummarizedMarketsModalEvent,
      userTimezone,
      widgetExpanded,
      toggleWidget,
    } = this.props;
    const { loading, loadingMarketData } = this.state;
    const isWidgetAvailable =
      unsummarizedMarketsModalEvent.widgets &&
      unsummarizedMarketsModalEvent.widgets.widgetProvider != null;

    return (
      <div className={`${loading ? "loading-markets" : ""}`}>
        <div
          className={`unsummarized-markets ${
            loadingMarketData ? "loading-market-data" : ""
          } `}
        >
          {this.state.loading ? (
            <LoadingSpinner />
          ) : (
            <React.Fragment>
              {!isWidgetAvailable ? (
                <WidgetNotAvailable
                  event={unsummarizedMarketsModalEvent}
                  userTimezone={userTimezone}
                  fetchEventData={this.fetchEventData}
                />
              ) : (
                <WidgetAvailable
                  event={unsummarizedMarketsModalEvent}
                  userTimezone={userTimezone}
                  fetchEventData={this.fetchEventData}
                  darkMode={darkMode}
                  widgetExpanded={widgetExpanded}
                  toggleWidget={toggleWidget}
                />
              )}

              {loadingMarketData && (
                <div className="spinner">
                  <LoadingSpinner />
                </div>
              )}
              <div className="unsummarized-markets__body">
                <Markets
                  event={unsummarizedMarketsModalEvent}
                  sportType={unsummarizedMarketsModalEvent.sportType}
                />
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  userTimezone: state.userModel.timezone,
  unsummarizedMarketsModalEvent:
    state.competitionListModel.unsummarizedMarketsModalEvent,
  marketGroupType: state.competitionListModel.marketGroupType,
  betslipMarketIds: state.betSlipListModel.marketIds,
  isIdle: state.sessionModel.isIdle,
  darkMode: state.sessionModel.darkMode,
  widgetExpanded: state.singleEventModel.widgetExpanded,
  user: state.userModel.user,
});

const mapDispatchToProps = dispatch => ({
  updateUnsummarizedMarketsModalEvent: (event: EventType) => {
    dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(event);
  },
  subscribeMarkets: (marketIdList: number[]) =>
    dispatch.subscribeModel.subscribeMarkets({
      marketIdList,
    }),
  unsubscribeMarkets: (marketIdList: number[]) =>
    dispatch.subscribeModel.unsubscribeMarkets({
      marketIdList,
    }),
  subscribeEvent: (eventId: number) =>
    dispatch.subscribeModel.subscribeEvent({
      eventId,
    }),
  unsubscribeEvent: (eventId: number) =>
    dispatch.subscribeModel.unsubscribeEvent({
      eventId,
    }),
  toggleWidget: (widgetExpanded: boolean) => {
    dispatch.singleEventModel.toggleWidget(widgetExpanded);
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UnsummarizedMarketsScreen) as any;
